import * as React from "react";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Switch,
  Tooltip,
  Spin,
} from "antd";
import axios from "axios";
import { responseNotification } from "../../utils/notify";
import { ImageUploader } from "../common";
import {
  QuestionCircleOutlined,
  LeftOutlined,
  CheckCircleFilled,
  EditOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";

type ShopBank = {
  id: string;
  bankingType: "BANKING" | "MOBILE_BANKING";
  bankName?: string; // BANKING হলে bankId, MOBILE_BANKING হলে BKASH/NAGAD
  branchName?: string; // branchId
  routingNumber?: string;
  accountName?: string;
  accountNumber?: string;
  isDefault?: boolean;
  uploadBankChequeCopy?: string;
};

export default function BankDetails() {
  const { token } = useSelector((state) => (state as any)?.authReducer);
  const navigate = useNavigate();

  const [merchantShopCheck, setMerchantShopCheck] = useState<any[]>([]);
  const shopId = merchantShopCheck?.[0]?.id || 0;

  const [banksLoading, setBanksLoading] = useState(false);
  const [shopBanks, setShopBanks] = useState<ShopBank[]>([]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ShopBank | null>(null);

  const [bankType, setBankType] = useState<"BANKING" | "MOBILE_BANKING">(
    "BANKING"
  );
  const [mobileBankingType, setMobileBankingType] = useState<string>("BKASH");

  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [branchId, setBranchId] = useState<any>();
  const [isDefault, setIsDefault] = useState(false);

  const [generatedImgUrl, setGeneratedImgUrl] = useState<string>();
  const [startUpload, setStartUpload] = useState<
    "Initiated" | "Uploading" | "Uploaded"
  >();

  const [loading, setLoading] = useState(false);
  const [error] = useState("");

  const [form] = Form.useForm();

  const [bankOptions, setBankOptions] = useState<{
    loading: boolean;
    list: any[];
    raw: any[];
  }>({ loading: false, list: [], raw: [] });

  // ==================== fetch shops ====================
  const getMerchantShopCheck = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_READER_API}/merchant/all-shops-by-merchant`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMerchantShopCheck(res?.data?.shops || []);
    } catch (e) {}
  }, [token]);

  // ==================== fetch shop banks ====================
  const fetchShopBanks = useCallback(async () => {
    if (!shopId) return;
    try {
      setBanksLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/getAllBank?shopId=${shopId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShopBanks(res?.data?.shopBanks || []);
    } catch (e) {
      setShopBanks([]);
    } finally {
      setBanksLoading(false);
    }
  }, [shopId, token]);

  // ==================== bank list (PAY API) ====================
  const getBankOptions = useCallback(
    async (key?: string) => {
      setBankOptions({ loading: true, list: [], raw: [] });

      const encodedUri = `${process.env.REACT_APP_PAY_API}`;
      let url = `?page=0&limit=20` + (key ? `&key=${key}` : ``);
      url = encodeURI(url);

      try {
        const res = await axios.get(`${encodedUri}/bank${url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const banks = res.data?.banks || [];
        setBankOptions({
          loading: false,
          raw: banks,
          list: banks.map((bank: any) => ({
            label: bank.name,
            value: bank.id,
          })),
        });
      } catch (err) {
        setBankOptions({ loading: false, list: [], raw: [] });
      }
    },
    [token]
  );

  const fetchRef = useRef(0);
  const handleSearch = useMemo(() => {
    const loadOptions = (value: string, field: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      if (fetchId !== fetchRef.current) return;
      if (value && field === "bank") getBankOptions(value);
    };
    return debounce(loadOptions, 800);
  }, [getBankOptions]);

  const handleBankChange = (bankId: string) => {
    setSelectedBankId(bankId);

    const selectedBank = bankOptions.raw.find(
      (bank: any) => bank.id === bankId
    );
    const branches = selectedBank?.branches || [];

    setBranchOptions(
      branches.map((b: any) => ({ label: b.name, value: b.id }))
    );
    setBranchId(undefined);
    form.setFieldsValue({ branchId: undefined });
  };

  // ==================== open modal (add) ====================
  const openAdd = () => {
    setEditing(null);
    setOpen(true);

    setBankType("BANKING");
    setMobileBankingType("BKASH");
    setSelectedBankId(null);
    setBranchOptions([]);
    setBranchId(undefined);
    setIsDefault(false);
    setGeneratedImgUrl(undefined);
    setStartUpload(undefined);

    form.resetFields();
    form.setFieldsValue({ bankType: "BANKING", isDefault: false });

    getBankOptions("");
  };

  // ==================== open modal (edit) ====================
  const openEdit = (item: ShopBank) => {
    setEditing(item);
    setOpen(true);

    const type = (item.bankingType || "BANKING") as
      | "BANKING"
      | "MOBILE_BANKING";
    setBankType(type);
    setIsDefault(!!item.isDefault);
    setGeneratedImgUrl(item.uploadBankChequeCopy || undefined);

    form.resetFields();

    if (type === "MOBILE_BANKING") {
      setMobileBankingType(item.bankName || "BKASH");
      form.setFieldsValue({
        bankType: "MOBILE_BANKING",
        accountNumber: item.accountNumber || "",
        accountName: item.accountName || "",
        isDefault: !!item.isDefault,
      });
    } else {
      getBankOptions("");
      setSelectedBankId(item.bankName || null);

      form.setFieldsValue({
        bankType: "BANKING",
        accountName: item.accountName || "",
        accountNumber: item.accountNumber || "",
        bankId: item.bankName || undefined,
        branchId: item.branchName || undefined,
        routingNumber: item.routingNumber || "",
        isDefault: !!item.isDefault,
      });

      // if raw already available, set branches
      const selectedBank = bankOptions.raw.find(
        (b: any) => b.id === item.bankName
      );
      const branches = selectedBank?.branches || [];
      setBranchOptions(
        branches.map((b: any) => ({ label: b.name, value: b.id }))
      );
      setBranchId(item.branchName);
    }
  };

  // submit after upload
  useEffect(() => {
    if (startUpload === "Uploaded") form.submit();
  }, [startUpload, form]);

  // ==================== submit ====================
  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const payload = {
        bankingType: bankType,
        accountName: `${data.accountName || ""}`,
        accountNumber: `${data.accountNumber || ""}`,
        bankName:
          bankType === "MOBILE_BANKING"
            ? mobileBankingType
            : selectedBankId || data.bankId || "",
        branchName:
          bankType === "MOBILE_BANKING" ? "" : branchId || data.branchId || "",
        routingNumber:
          bankType === "MOBILE_BANKING" ? "" : `${data.routingNumber || ""}`,
        isDefault: !!isDefault,
        shopId: shopId,
        uploadBankChequeCopy: generatedImgUrl ? generatedImgUrl : "",
      };

      const url = editing?.id
        ? `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/update-bank`
        : `${process.env.REACT_APP_CATALOG_WRITER_API}/shop-bank/add-bank`;

      const method = editing?.id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          editing?.id ? { ...payload, id: editing.id } : payload
        ),
      });

      const json = await res.json();

      if (json?.statusCode === 200) {
        responseNotification(
          editing?.id ? "Updated Successfully" : "Created Successfully",
          "success"
        );
        setOpen(false);
        setEditing(null);
        form.resetFields();
        setGeneratedImgUrl(undefined);
        setStartUpload(undefined);
        await fetchShopBanks();
      } else {
        responseNotification(json?.message || "something wrong", "warning");
      }
    } catch (e) {
      responseNotification("Internal server error", "error");
    } finally {
      setLoading(false);
    }
  };

  // ==================== effects ====================
  useEffect(() => {
    getMerchantShopCheck();
  }, [getMerchantShopCheck]);

  useEffect(() => {
    if (shopId) fetchShopBanks();
  }, [shopId, fetchShopBanks]);

  // when bank options loaded + editing has bankName, set branch list properly
  useEffect(() => {
    if (!open) return;
    if (bankType !== "BANKING") return;

    const bankId = editing?.bankName || selectedBankId;
    if (!bankId) return;

    const selectedBank = bankOptions.raw.find((b: any) => b.id === bankId);
    const branches = selectedBank?.branches || [];
    setBranchOptions(
      branches.map((b: any) => ({ label: b.name, value: b.id }))
    );
  }, [bankOptions.raw, editing, selectedBankId, open, bankType]);

  // ==================== card renderer ====================
  const renderCard = (item: ShopBank) => {
    const isDef = !!item.isDefault;

    const bankTitle =
      item.bankingType === "MOBILE_BANKING"
        ? item.bankName || "MOBILE_BANKING"
        : bankOptions.raw.find((b: any) => b.id === item.bankName)?.name ||
          "Bank";

    const sub =
      item.bankingType === "MOBILE_BANKING" ? "MOBILE_BANKING" : "BANKING";

    return (
      <div
        key={item.id}
        role="button"
        tabIndex={0}
        onClick={() => openEdit(item)}
        onKeyDown={(e) => e.key === "Enter" && openEdit(item)}
        className={[
          "w-full rounded-2xl border p-4 shadow-sm transition cursor-pointer",
          "bg-white",
          isDef
            ? "border-teal-300 bg-teal-50/40"
            : "border-gray-200 hover:border-gray-300",
        ].join(" ")}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
              <span className="text-teal-700 font-bold">🏦</span>
            </div>
            <div>
              <div className="text-lg font-extrabold text-gray-900 leading-5">
                {bankTitle}
              </div>
              <div className="text-teal-700 font-semibold text-xs mt-1">
                {sub}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isDef && (
              <div className="text-teal-700 font-bold text-sm flex items-center gap-2">
                <span>DEFAULT</span>
                <CheckCircleFilled className="text-teal-600" />
              </div>
            )}
            <Button
              size="small"
              icon={<EditOutlined />}
              className="!rounded-xl !font-semibold"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openEdit(item);
              }}
            >
              Edit
            </Button>
          </div>
        </div>

        <div className="border-t border-gray-200 my-3" />

        <div className="space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-gray-400">Account Name</span>
            <span className="text-gray-900 font-semibold truncate max-w-[55%]">
              {item.accountName || "-"}
            </span>
          </div>

          <div className="flex justify-between gap-3">
            <span className="text-gray-400">Account Number</span>
            <span className="text-gray-900 font-semibold">
              {item.accountNumber || "-"}
            </span>
          </div>

          {item.bankingType === "BANKING" && (
            <div className="flex justify-between gap-3">
              <span className="text-gray-400">Routing Number</span>
              <span className="text-gray-900 font-semibold">
                {item.routingNumber || "-"}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ==================== UI ====================
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-gray-100/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-md mx-auto py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-3xl flex items-center gap-2 text-gray-800 font-semibold"
          >
            <LeftOutlined />
            <span>Bank Accounts</span>
          </button>
          {(shopBanks?.length ?? 0) > 0 && (
            <Button
              type="primary"
              onClick={openAdd}
              className="!bg-rose-600 !border-rose-600 !rounded-xl !font-bold"
            >
              Add More
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-4">
        {banksLoading ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : shopBanks?.length ? (
          <div className="space-y-4">{shopBanks.map(renderCard)}</div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 text-center text-gray-600">
            No bank accounts found.
            <div className="mt-3">
              <Button
                type="primary"
                onClick={openAdd}
                className="!bg-rose-600 !border-rose-600 !rounded-xl !font-bold"
              >
                Add Bank Account
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <EditOutlined />
            <span>
              {editing?.id ? "Edit Bank Account" : "Add Bank Account"}
            </span>
          </div>
        }
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        footer={null}
        destroyOnClose
        centered
      >
        <Form name="bankForm" onFinish={onSubmit} form={form} layout="vertical">
          <div className="flex justify-between border-b pb-3 mb-5 border-gray-100">
            <Form.Item name="bankType" initialValue={bankType} className="mb-0">
              <Radio.Group
                value={bankType}
                onChange={(e) => {
                  const val = e.target.value as "BANKING" | "MOBILE_BANKING";
                  setBankType(val);
                  form.setFieldsValue({ bankType: val });

                  setSelectedBankId(null);
                  setBranchOptions([]);
                  setBranchId(undefined);
                  form.setFieldsValue({
                    bankId: undefined,
                    branchId: undefined,
                    routingNumber: "",
                  });
                }}
              >
                <Radio value="BANKING">BANK</Radio>
                <Radio value="MOBILE_BANKING">MOBILE BANKING (MFS)</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          {bankType === "MOBILE_BANKING" ? (
            <>
              <Form.Item
                hasFeedback
                label="Mobile Banking Type"
                name="mfsType"
                initialValue={mobileBankingType}
                rules={[
                  {
                    required: true,
                    message: "Mobile Banking Type is required!",
                  },
                ]}
              >
                <Select
                  value={mobileBankingType}
                  onChange={(val) => setMobileBankingType(val)}
                  options={[
                    { value: "BKASH", label: "BKASH" },
                    { value: "NAGAD", label: "NAGAD" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="accountNumber"
                hasFeedback
                label="Mobile Number"
                rules={[
                  { required: true, message: "Mobile Number is Required!" },
                ]}
              >
                <Input placeholder="Enter mobile number" />
              </Form.Item>

              <Form.Item
                name="accountName"
                hasFeedback
                label="Full Name"
                rules={[{ required: true, message: "Full Name is Required!" }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="accountName"
                hasFeedback
                label="Account Name"
                rules={[
                  { required: true, message: "Account Name is Required!" },
                ]}
              >
                <Input placeholder="Enter account name" />
              </Form.Item>

              <Form.Item
                name="accountNumber"
                hasFeedback
                label="Account Number"
                rules={[
                  { required: true, message: "Account Number is Required!" },
                ]}
              >
                <Input placeholder="Enter account number" />
              </Form.Item>

              <Form.Item
                name="bankId"
                label="Bank"
                rules={[{ required: true, message: "Bank is required!" }]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Bank"
                  loading={bankOptions.loading}
                  options={bankOptions.list}
                  value={selectedBankId || undefined}
                  onChange={(val) => handleBankChange(val)}
                  onSearch={(e) => handleSearch(e, "bank")}
                  filterOption={false}
                />
              </Form.Item>

              <Form.Item
                name="branchId"
                label="Branch"
                rules={[{ required: true, message: "Branch is required!" }]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder={
                    selectedBankId ? "Select Branch" : "Select Bank First"
                  }
                  value={branchId}
                  onChange={(val) => setBranchId(val)}
                  disabled={!selectedBankId}
                  options={branchOptions}
                />
              </Form.Item>

              <Form.Item
                name="routingNumber"
                hasFeedback
                label="Routing Number"
                rules={[
                  { required: true, message: "Routing Number is Required!" },
                ]}
              >
                <Input placeholder="Enter routing number" />
              </Form.Item>

              <div className="mb-4">
                <Divider orientation="left">
                  Upload Bank Cheque Copy
                  <Tooltip
                    placement="bottom"
                    title="Upload Bank Cheque Copy should be clear image"
                  >
                    <QuestionCircleOutlined className="px-2 -mt-2" />
                  </Tooltip>
                </Divider>

                <ImageUploader
                  imgUrl={generatedImgUrl || ""}
                  startUpload={startUpload}
                  setStartUpload={(val: any) => setStartUpload(val)}
                  setGeneratedImgUrl={(url: any) => setGeneratedImgUrl(url)}
                />
              </div>
            </>
          )}

          <Form.Item
            label="Default?"
            name="isDefault"
            style={{ minWidth: "100%" }}
          >
            <Switch
              checkedChildren="Yes"
              unCheckedChildren="No"
              checked={isDefault}
              onChange={setIsDefault}
            />
          </Form.Item>

          <div className="flex gap-3 justify-end mt-6">
            <Button
              onClick={() => {
                form.resetFields();
                setGeneratedImgUrl(undefined);
                setStartUpload(undefined);
                setIsDefault(false);
                setBranchId(undefined);
                setSelectedBankId(null);
              }}
              className="!rounded-xl"
            >
              Reset
            </Button>

            <Button
              type="primary"
              loading={loading}
              disabled={loading || !!error}
              onClick={(e) => {
                e.preventDefault();
                form.submit();
              }}
              className="!bg-rose-600 !border-rose-600 !rounded-xl !font-bold"
              icon={<EditOutlined />}
            >
              {editing?.id ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
